import { useCallback, useEffect, useMemo } from 'react';

import * as Sentry from '@sentry/browser';

import { useTemplateQuery } from '../data/useTemplateQuery';
import type { SectionId } from '../model/operations';
import { sortSections } from '../model/templateProjection';
import {
  useSectionScrollNavigation,
  type SectionScrollNavigation,
} from '../navigation/useSectionScrollNavigation';
import { renderedTemplate } from '../state/selectors';
import { useCustomizationDispatch } from '../state/useCustomizationDispatch';
import { useCustomizationState } from '../state/useCustomizationState';
import { useGetLatestCustomizationState } from '../state/useGetLatestCustomizationState';
import {
  createTemplateCustomizationViewModel,
  type TemplateCustomizationViewModel,
} from '../viewmodels/createTemplateCustomizationViewModel';
import {
  resolvePageStatus,
  type PageStatus,
} from '../viewmodels/resolvePageStatus';
import { resolveRefreshAction } from './resolveRefreshAction';
import { useTemplatePermissions } from './useTemplatePermissions';

export type TemplateCustomization = SectionScrollNavigation & {
  status: PageStatus;
  viewModel: TemplateCustomizationViewModel | null;
  handleRetry: () => void;
  handleRefresh: () => Promise<void>;
};

const NO_SECTION_IDS: SectionId[] = [];

export function useTemplateCustomization(): TemplateCustomization {
  const {
    templateId,
    channelId,
    replica,
    replicaStatus,
    expandedBySectionId,
    activeSectionId,
    pendingOps,
    failures,
    staleReason,
    refreshAttempt,
  } = useCustomizationState();
  const permissions = useTemplatePermissions();
  const dispatch = useCustomizationDispatch();
  const getLatestState = useGetLatestCustomizationState();
  const { template, failure, load, refresh } = useTemplateQuery({
    channelId,
    templateId,
  });

  useEffect(() => {
    if (template) {
      dispatch({ type: 'REPLICA_LOADED', template });
    }
  }, [template, dispatch]);

  useEffect(() => {
    if (failure) {
      dispatch({ type: 'REPLICA_LOAD_FAILED', failure });
    }
  }, [failure, dispatch]);

  const handleRetry = useCallback(() => {
    dispatch({ type: 'REPLICA_LOAD_STARTED' });
    load();
  }, [dispatch, load]);

  const handleRefresh = useCallback(async () => {
    const {
      templateId: requestedTemplateId,
      channelId: requestedChannelId,
      refreshAttempt: attempt,
    } = getLatestState();

    if (attempt.status === 'inFlight') {
      return;
    }

    dispatch({ type: 'REFRESH_REQUESTED' });

    const outcome = await refresh();

    if (!outcome.ok) {
      Sentry.captureException(outcome.cause, {
        tags: { operation: 'refresh', outcome: outcome.failure },
        extra: {
          templateId: requestedTemplateId,
          channelId: requestedChannelId,
        },
      });
    }

    // A template switch resets the state, so the result belongs to a page that is gone
    if (getLatestState().templateId !== requestedTemplateId) {
      return;
    }

    const action = resolveRefreshAction(outcome);

    dispatch(action);
  }, [getLatestState, dispatch, refresh]);

  const status = resolvePageStatus({
    hasChannel: Boolean(channelId),
    hasTemplateId: Boolean(templateId),
    replicaStatus,
  });

  const rendered = useMemo(
    () => renderedTemplate({ replica, pendingOps }),
    [replica, pendingOps]
  );

  const sectionIds = useMemo(() => {
    if (!rendered || status !== 'ready') {
      return NO_SECTION_IDS;
    }

    const sortedSections = sortSections(rendered.sections);

    return sortedSections.map(section => section.id);
  }, [rendered, status]);

  const { registerSection, handleSectionSelect } =
    useSectionScrollNavigation(sectionIds);

  const viewModel = useMemo(
    () =>
      replica && status === 'ready'
        ? createTemplateCustomizationViewModel({
            template: replica,
            expandedBySectionId,
            activeSectionId,
            pendingOps,
            failures,
            staleReason,
            refreshAttempt,
            permissions,
          })
        : null,
    [
      replica,
      status,
      expandedBySectionId,
      activeSectionId,
      pendingOps,
      failures,
      staleReason,
      refreshAttempt,
      permissions,
    ]
  );

  return {
    status,
    viewModel,
    handleRetry,
    handleRefresh,
    registerSection,
    handleSectionSelect,
  };
}
